import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Checkbox, Collapse, Divider, IconButton, Link, Typography } from '@mui/material';
import { lighten } from '@mui/material/styles';
import React from 'react';
import { handleErrorSnackbar } from '../../SnackbarHelper';
import { useCommonWebTranslation } from '../../hooks';
export const Content = React.memo(ContentComponent);
/**
 * Renders a hierarchical tree of items with support for selection, filtering, expansion,
 * and child-level controls such as "select all". It handles initial loading, state updates,
 * and conditionally renders multi-select and child tree items.
 * @template T
 * @param {DataTreeProps<T> & { appliedFilter: FieldFilter[] }} props - Props for configuring and rendering the data tree, including filter and selection logic.
 * @returns {React.ReactElement} The rendered content component for the data tree.
 */
function ContentComponent(props) {
    var _a;
    const initialMultiSelectedIds = React.useRef(props.multiSelectedItemIds);
    const [items, setItems] = React.useState([]);
    const [expandedIds, setExpandedIds] = React.useState([]);
    const [highlightedIds, setHighlightedIds] = React.useState([]);
    const [disabledIds, setDisabledIds] = React.useState([]);
    const [fullyDisabledIds, setFullyDisabledIds] = React.useState([]);
    const reload = React.useCallback(() => {
        props.service
            .list(props.appliedFilter)
            .then((filtered) => {
            setItems(filtered);
            const [calcExpandedIds, calcHighlightedIds, calcDisabledIds, calcFullyDisabledIds] = calculateIds(props.service, filtered, initialMultiSelectedIds.current);
            setExpandedIds(calcExpandedIds);
            setHighlightedIds(calcHighlightedIds);
            setDisabledIds(calcDisabledIds);
            setFullyDisabledIds(calcFullyDisabledIds);
            initialMultiSelectedIds.current = undefined;
        })
            .catch(handleErrorSnackbar);
    }, [props.service, props.appliedFilter]);
    React.useEffect(() => {
        reload();
    }, [reload, props.reloadFlag]);
    let childrenExist = false;
    for (const item of items) {
        const children = props.service.children(item);
        if (children && children.length > 0) {
            childrenExist = true;
        }
    }
    return (React.createElement(Typography, { component: 'div', sx: { width: '100%', height: '100%', overflow: 'auto' }, "data-test": 'content' },
        props.showSelectAll && props.onMultiSelectChange && props.multiSelect && (React.createElement(React.Fragment, null,
            React.createElement(Divider, null),
            React.createElement(SelectAllTreeItem, { selectAllLabel: props.selectAllLabel, disabledCheckbox: props.disabledCheckboxes, itemsIds: collectAllSelectableIds(items, props.service), multiSelectedItemIds: (_a = props.multiSelectedItemIds) !== null && _a !== void 0 ? _a : [], onMultiSelectChange: props.onMultiSelectChange }))),
        React.createElement(Divider, null),
        items.map((item) => {
            const id = props.service.id(item);
            return (React.createElement(React.Fragment, { key: id },
                React.createElement(DataTreeItem, { service: props.service, level: 0, id: id, item: item, childrenExist: childrenExist, multiSelect: props.multiSelect, selectedItemId: props.selectedItemId, multiSelectedItemIds: props.multiSelectedItemIds, expandedIds: expandedIds, highlightedIds: highlightedIds, disabledIds: disabledIds, fullyDisabledIds: fullyDisabledIds, disabledCheckbox: props.disabledCheckboxes, renderItem: props.renderItem, onSelectItem: props.onSelectItem, onMultiSelectChange: props.onMultiSelectChange, onExpand: (expandedId) => setExpandedIds([...expandedIds, expandedId]), onCollapse: (collapsedId) => setExpandedIds(expandedIds.filter((expId) => expId !== collapsedId)), showSelectAllChildren: props.showSelectAllChildren }),
                React.createElement(Divider, null)));
        })));
}
/**
 * A tree item component used to select or deselect all child items within a tree structure.
 *
 * @param {SelectAllTreeItemProps} props - The props for the SelectAllTreeItem component.
 * @param {React.ReactNode} [props.selectAllLabel] - Optional custom label to display next to the checkbox.
 * @param {boolean} [props.disabledCheckbox] - Whether the select-all checkbox should be disabled.
 * @param {string[]} props.itemsIds - List of child item IDs that can be selected.
 * @param {string[]} props.multiSelectedItemIds - List of currently selected item IDs.
 * @param {(itemIds: string[]) => void} props.onMultiSelectChange - Callback invoked with updated selection when checkbox changes.
 *
 * @returns {JSX.Element} The rendered Select All tree item JSX element.
 */
export const SelectAllTreeItem = (props) => {
    const [selectedAll, setSelectedAll] = React.useState(false);
    const { t } = useCommonWebTranslation();
    React.useEffect(() => {
        setSelectedAll(areAllItemsSelected(props.multiSelectedItemIds, props.itemsIds));
    }, [props.itemsIds, props.multiSelectedItemIds]);
    const handleSelectAll = (checked) => {
        setSelectedAll(!selectedAll);
        if (checked) {
            const result = Array.from(new Set([...props.multiSelectedItemIds, ...props.itemsIds]));
            props.onMultiSelectChange(result);
        }
        else if (props.multiSelectedItemIds) {
            const result = props.multiSelectedItemIds.filter((selectedItemId) => !props.itemsIds.includes(selectedItemId));
            props.onMultiSelectChange(result);
        }
    };
    return (React.createElement(Box, { sx: {
            display: 'flex',
            alignItems: 'center',
            minHeight: 48,
            cursor: 'default',
            '&:hover': {
                backgroundColor: (theme) => theme.palette.action.hover,
            },
            '&:focus': {
                outline: 'none',
            },
        } },
        React.createElement(Box, { sx: {
                minWidth: { xs: 24, sm: 48 },
                marginRight: 1,
            } },
            React.createElement(Checkbox, { sx: {
                    padding: { xs: 0, sm: 1 },
                }, disabled: props.disabledCheckbox || props.itemsIds.length === 0, color: 'primary', checked: selectedAll, onClick: (event) => event.stopPropagation(), onChange: (event) => handleSelectAll(event.target.checked), inputProps: { 'aria-label': t('common.selectAll') }, "data-test": 'select-all-checkbox' })),
        React.createElement(Box, { display: 'flex', alignItems: 'center', flexGrow: 1 }, props.selectAllLabel || 'Select All')));
};
/**
 * Checks whether all items in the target array are present in the source array.
 * @param {string[]} arr - The source array to check against.
 * @param {string[]} target - The array of items to verify as selected.
 * @returns {boolean} True if all target items are included in the source array; otherwise, false.
 */
function areAllItemsSelected(arr, target) {
    return target.every((v) => arr.includes(v));
}
/**
 * Renders a single node in a hierarchical data tree, supporting expansion, selection, highlighting,
 * and recursive rendering of children.
 * @template T
 * @param {DataTreeItemProps<T>} props - Props for configuring the behavior, structure, and rendering of the tree item.
 * @returns {React.ReactElement} The rendered tree item component.
 */
function DataTreeItem(props) {
    const checked = props.multiSelectedItemIds && props.multiSelectedItemIds.includes(props.id);
    const expanded = props.expandedIds.includes(props.id);
    const highlighted = props.highlightedIds.includes(props.id);
    const disabled = props.disabledIds.includes(props.id);
    const fullyDisabled = props.fullyDisabledIds ? props.fullyDisabledIds.includes(props.id) : false;
    const children = props.service.children(props.item);
    const { t } = useCommonWebTranslation();
    const childrenIds = React.useMemo(() => children && collectAllSelectableIds(children, props.service), [children, props.service]);
    const allChildrenSelected = children && children.length > 0 && (childrenIds === null || childrenIds === void 0 ? void 0 : childrenIds.every((id) => { var _a; return (_a = props.multiSelectedItemIds) === null || _a === void 0 ? void 0 : _a.includes(id); }));
    const atLeastOneChildSelected = children && children.length > 0 && childrenIds && (childrenIds === null || childrenIds === void 0 ? void 0 : childrenIds.some((id) => { var _a; return (_a = props.multiSelectedItemIds) === null || _a === void 0 ? void 0 : _a.includes(id); }));
    const [mouseOverItem, setMouseOverItem] = React.useState(false);
    const handleSelectAllChildren = () => {
        var _a;
        if (props.onMultiSelectChange && childrenIds) {
            if (!allChildrenSelected) {
                const result = [...((_a = props.multiSelectedItemIds) !== null && _a !== void 0 ? _a : []), ...childrenIds];
                if (!disabled) {
                    result.push(props.id);
                }
                props.onMultiSelectChange(result);
            }
            else if (props.multiSelectedItemIds) {
                let result = props.multiSelectedItemIds.filter((selectedItemId) => !childrenIds.includes(selectedItemId));
                if (!disabled) {
                    result = result.filter((selectedItemId) => selectedItemId !== props.id);
                }
                props.onMultiSelectChange(result);
            }
        }
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(Box, { sx: {
                display: 'flex',
                alignItems: 'center',
                minHeight: 48,
                cursor: 'default',
                paddingLeft: {
                    xs: props.level * 2,
                    sm: props.level * 4,
                },
                color: fullyDisabled ? 'text.disabled' : 'dark',
                backgroundColor: (theme) => props.selectedItemId === props.id
                    ? highlighted
                        ? lighten(theme.palette.primary.dark, 0.76)
                        : theme.palette.action.selected
                    : highlighted
                        ? lighten(theme.palette.primary.dark, 0.84)
                        : undefined,
                '&:hover': {
                    backgroundColor: (theme) => fullyDisabled
                        ? undefined
                        : props.selectedItemId === props.id
                            ? highlighted
                                ? lighten(theme.palette.primary.dark, 0.68)
                                : theme.palette.action.selected
                            : highlighted
                                ? lighten(theme.palette.primary.dark, 0.84)
                                : theme.palette.action.hover,
                },
                '&:focus': {
                    outline: 'none',
                },
            }, onClick: () => {
                if (props.onSelectItem) {
                    if (!fullyDisabled) {
                        if (!disabled) {
                            props.onSelectItem(props.item);
                        }
                        if (!expanded) {
                            props.onExpand(props.id);
                        }
                    }
                }
            }, "data-test": 'row', onMouseOver: () => setMouseOverItem(true), onMouseOut: () => setMouseOverItem(false) },
            props.childrenExist && (React.createElement(Box, { sx: {
                    minWidth: 48,
                    marginRight: { xs: 0, sm: 1 },
                } }, children && children.length > 0 && (React.createElement(IconButton, { onClick: (event) => {
                    event.stopPropagation();
                    if (!fullyDisabled) {
                        if (expanded) {
                            props.onCollapse(props.id);
                        }
                        else {
                            props.onExpand(props.id);
                        }
                    }
                }, "aria-label": t('common.expand'), "data-test": 'expand-button', size: 'large' }, expanded ? React.createElement(ExpandLessIcon, null) : React.createElement(ExpandMoreIcon, null))))),
            props.multiSelect && (React.createElement(Box, { sx: {
                    minWidth: { xs: 24, sm: 48 },
                    marginRight: 1,
                } },
                React.createElement(Checkbox, { indeterminate: atLeastOneChildSelected && !checked, sx: {
                        padding: { xs: 0, sm: 1 },
                    }, disabled: disabled || props.disabledCheckbox || fullyDisabled, color: 'primary', checked: checked, onClick: (event) => event.stopPropagation(), onChange: (event) => {
                        var _a;
                        if (props.onMultiSelectChange) {
                            if (event.target.checked) {
                                props.onMultiSelectChange([...((_a = props.multiSelectedItemIds) !== null && _a !== void 0 ? _a : []), props.id]);
                            }
                            else if (props.multiSelectedItemIds) {
                                props.onMultiSelectChange(props.multiSelectedItemIds.filter((selectedItemId) => selectedItemId !== props.id));
                            }
                        }
                    }, inputProps: { 'aria-label': t('common.selectCheckbox') }, "data-test": 'select-checkbox' }))),
            React.createElement(Box, { sx: {
                    display: 'flex',
                    alignItems: 'center',
                    flexGrow: 1,
                    marginLeft: !props.childrenExist && !props.multiSelect ? 2 : 0,
                }, "data-test": 'content' }, props.renderItem(props.item)),
            props.multiSelect && childrenIds && childrenIds.length > 0 && props.showSelectAllChildren && (React.createElement(Link, { onClick: () => handleSelectAllChildren(), sx: { display: mouseOverItem ? 'block' : 'none', pr: 2 }, component: 'button', variant: 'body2', "data-test": 'select-all-button' }, allChildrenSelected ? 'Deselect All' : 'Select All'))),
        children && children.length > 0 && (React.createElement(Collapse, { in: expanded, mountOnEnter: true, unmountOnExit: true, "data-test": 'children' }, children.map((item) => {
            const id = props.service.id(item);
            return (React.createElement(DataTreeItem, { key: id, service: props.service, level: props.level + 1, id: id, item: item, childrenExist: true, multiSelect: props.multiSelect, selectedItemId: props.selectedItemId, multiSelectedItemIds: props.multiSelectedItemIds, expandedIds: props.expandedIds, highlightedIds: props.highlightedIds, disabledIds: props.disabledIds, fullyDisabledIds: props.fullyDisabledIds, renderItem: props.renderItem, onSelectItem: props.onSelectItem, onMultiSelectChange: props.onMultiSelectChange, onExpand: props.onExpand, onCollapse: props.onCollapse, showSelectAllChildren: props.showSelectAllChildren }));
        })))));
}
/**
 * Collects all selectable (i.e. Not disabled) item IDs from a hierarchical tree structure.
 * @template T
 * @param {T[]} items - The root-level items to traverse.
 * @param {DataTreeService<T>} service - The service used to access item metadata and child relationships.
 * @returns {string[]} ItemIds - An array of IDs for all selectable items.
 */
function collectAllSelectableIds(items, service) {
    const itemsIds = [];
    /**
     * Recursively collects the IDs of all non-disabled items in a tree structure.
     * @template T
     * @param {T} obj - The current item being traversed.
     */
    function flatten(obj) {
        var _a;
        if (!((_a = service.disable) === null || _a === void 0 ? void 0 : _a.call(service, obj))) {
            itemsIds.push(service.id(obj));
        }
        const children = service.children(obj);
        if (!children || children.length === 0) {
            return;
        }
        children.forEach(flatten);
    }
    items.forEach(flatten);
    return itemsIds;
}
/**
 * Traverses a tree of items to calculate and return arrays of expanded, highlighted, disabled,
 * and fully disabled item IDs, ensuring their parent paths are included in expanded IDs.
 * @template T
 * @param {DataTreeService<T>} service - The service providing methods for tree traversal and item evaluation.
 * @param {T[]} items - The root-level items to traverse.
 * @param {string[]} [multiSelectItemsIds] - Optional list of selected item IDs to expand their ancestor paths.
 * @returns {[string[], string[], string[], string[]]} A tuple containing arrays of expanded IDs, highlighted IDs, disabled IDs, and fully disabled IDs.
 */
function calculateIds(service, items, multiSelectItemsIds) {
    const parentIdsMap = {};
    const highlightedIds = [];
    const disabledIds = [];
    const fullyDisabledIds = [];
    const expandedIds = [];
    for (const item of items) {
        walkItemTree(service, item, [], parentIdsMap, expandedIds, highlightedIds, disabledIds, fullyDisabledIds);
    }
    if (multiSelectItemsIds) {
        for (const selectedId of multiSelectItemsIds) {
            const parentIds = parentIdsMap[selectedId];
            if (parentIds) {
                for (const parentId of parentIds) {
                    if (!expandedIds.includes(parentId)) {
                        expandedIds.push(parentId);
                    }
                }
            }
        }
    }
    for (const highlightedId of highlightedIds) {
        const parentIds = parentIdsMap[highlightedId];
        if (parentIds) {
            for (const parentId of parentIds) {
                if (!expandedIds.includes(parentId)) {
                    expandedIds.push(parentId);
                }
            }
        }
    }
    for (const expandedId of expandedIds) {
        const parentIds = parentIdsMap[expandedId];
        if (parentIds) {
            for (const parentId of parentIds) {
                if (!expandedIds.includes(parentId)) {
                    expandedIds.push(parentId);
                }
            }
        }
    }
    return [expandedIds, highlightedIds, disabledIds, fullyDisabledIds];
}
/**
 * Recursively traverses a tree structure of items and populates metadata arrays and a parent mapping.
 * @template T
 * @param {DataTreeService<T>} service - The service providing item access methods like `id`, `children`, `highlight`, etc.
 * @param {T} item - The current tree item being processed.
 * @param {string[]} parentIds - The list of ancestor IDs for the current item.
 * @param {{ [id: string]: string[] }} parentIdsMap - A mapping of item IDs to their ancestor ID chains.
 * @param {string[]} expandedIds - The array to collect IDs of items marked for expansion.
 * @param {string[]} highlightedIds - The array to collect IDs of items marked as highlighted.
 * @param {string[]} disabledIds - The array to collect IDs of items marked as disabled.
 * @param {string[]} fullyDisabledIds - The array to collect IDs of items fully disabled (non-interactable).
 */
function walkItemTree(service, item, parentIds, parentIdsMap, expandedIds, highlightedIds, disabledIds, fullyDisabledIds) {
    var _a, _b, _c, _d, _e;
    const id = service.id(item);
    parentIdsMap[id] = [...parentIds, ...((_a = parentIdsMap[id]) !== null && _a !== void 0 ? _a : [])];
    const children = service.children(item);
    if ((_b = service.highlight) === null || _b === void 0 ? void 0 : _b.call(service, item)) {
        highlightedIds.push(id);
    }
    if ((_c = service.disable) === null || _c === void 0 ? void 0 : _c.call(service, item)) {
        disabledIds.push(id);
    }
    if ((_d = service.fullyDisable) === null || _d === void 0 ? void 0 : _d.call(service, item)) {
        fullyDisabledIds.push(id);
    }
    if ((_e = service.expand) === null || _e === void 0 ? void 0 : _e.call(service, item)) {
        expandedIds.push(id);
    }
    if (children) {
        for (const child of children) {
            walkItemTree(service, child, [...parentIds, id], parentIdsMap, expandedIds, highlightedIds, disabledIds, fullyDisabledIds);
        }
    }
}
